<template>
  <nav class="main-nav flex justify-center">
    <template v-for="item in menuItems" :key="item.key">
      <NuxtLink
        v-if="!item.scrollTo"
        :to="item.href"
        class="nav-link"
        :class="{ 'nav-link-active': isItemActive(item) }"
      >
        {{ item.label }}
      </NuxtLink>
      <NuxtLink
        v-else
        :to="{ path: `/${currentLocale}`, hash: `#${item.scrollTo}` }"
        class="nav-link"
        :class="{ 'nav-link-active': isItemActive(item) }"
        @click.prevent="goToSection(item.scrollTo!)"
      >
        {{ item.label }}
      </NuxtLink>
    </template>
  </nav>
</template>

<script setup lang="ts">
const router = useRouter()
const route = useRoute()
const { t, locale } = useI18n()

const currentLocale = computed(() => (route.params.locale ?? locale.value) as string)

type MenuItem = { key: string; label: string; href?: string; scrollTo?: string }

const menuItems = computed(() => {
  const items: MenuItem[] = [
    { key: 'home', label: t('layout.header.navigation.home'), href: `/${currentLocale.value}` },
    { key: 'courses', label: t('layout.header.navigation.courses'), href: `/${currentLocale.value}`, scrollTo: 'courses-section' },
    { key: 'about', label: t('layout.header.navigation.about'), href: `/${currentLocale.value}`, scrollTo: 'about-us-section' },
    // { key: 'blog', label: t('layout.header.navigation.blog'), href: `/${currentLocale.value}/articles` },
    // { key: 'events', label: t('layout.header.navigation.events'), href: `/${currentLocale.value}/events` },
  ]
  items.push({ key: 'contacts', label: t('layout.header.navigation.contacts'), href: `/${currentLocale.value}/contacts` })
  return items
})

function isItemActive (item: MenuItem) {
  const homePath = `/${currentLocale.value}`
  if (item.scrollTo) {
    return route.path === homePath && route.hash === `#${item.scrollTo}`
  }
  if (item.href === homePath && !item.scrollTo) {
    return route.path === homePath && !route.hash
  }
  return route.path === item.href || (item.href !== homePath && route.path.startsWith(item.href ?? ''))
}

function goToSection (sectionId: string) {
  const homePath = `/${currentLocale.value}`
  if (route.path === homePath) {
    const el = document.getElementById(sectionId)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
      router.replace({ path: homePath, hash: `#${sectionId}` })
    } else {
      router.push({ path: homePath, hash: `#${sectionId}` })
    }
  } else {
    router.push({ path: homePath, hash: `#${sectionId}` })
  }
}
</script>

<style scoped>
.main-nav {
  display: flex;
  align-items: flex-start;
  gap: 40px;
  width: -webkit-fill-available;
}

.nav-link {
  font-family: var(--font-inter);
  font-weight: 500;
  font-size: 16px;
  color: #252840;
  text-decoration: none;
  transition: color 0.2s ease;
}

.nav-link:hover,
.nav-link.nav-link-active,
.nav-link-button:hover {
  color: #00CE9B;
}

.nav-link-button {
  background: none;
  border: none;
  cursor: pointer;
}

@media (max-width: 1300px) {
  .main-nav {
    display: none;
  }
}
</style>
